import { Settings } from '.generated/Feature.CW.model';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import {
  CoveoEngineProvider,
  useCoveoEngine,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import React, { useRef } from 'react';
import { useEffect, useState } from 'react';
import { SearchBar, LocationInputHandle } from '../../search/GlobalSearch';

type PageSettings = Settings.SiteSettings['fields'];

export const SearchOverlay = ({
  handleSearchToggle,
}: {
  handleSearchToggle: (isOpen: boolean) => void;
}) => {
  // const { searchOverlay } = TAILWIND_VARIANTS();
  const { getDictionaryValue } = useDictionary();
  const { sitecoreContext } = useSitecoreContext();
  const [isConfigReady, setIsConfigReady] = useState(false);
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;
  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
  });
  const dropDownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      contextWithSettings?.globalSearchAccessToken?.value &&
      contextWithSettings?.globalSearchSearchHub?.value
    ) {
      setCoveoConfig({
        searchHub: contextWithSettings.globalSearchSearchHub.value,
        pipeline: contextWithSettings.globalSearchPipeline?.value || '',
        accessToken: contextWithSettings.globalSearchAccessToken.value,
        organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
      });
      setIsConfigReady(true);
    }
  }, [contextWithSettings]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSearchToggle(false);
      }
    };
    const handleModelEvent = (e: MouseEvent) => {
      if (dropDownRef.current && !dropDownRef.current.contains(e.target as Node)) {
        handleSearchToggle(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleModelEvent);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleModelEvent);
    };
  }, []);

  // Prevent rendering unless config is complete
  if (!isConfigReady) {
    return null;
  }

  return (
    <CoveoEngineProvider config={coveoConfig}>
      <div className="lg:absolute inset-0 z-10 transition-all lg:top-full">
        <div className="bg-white lg:border-b-4 lg:border-blue-base pt-16 pb-9" ref={dropDownRef}>
          <div className=" max-w-screen-xxl w-full lg:px-20 px-10 mx-auto">
            <h3 className="text-26 leading-[32px] font-centuryGothic font-bold text-gray-900 mb-12">
              <PlainTextWrapper field={{ value: getDictionaryValue('GlobalSearchHeading') }} />
            </h3>
            <SearchContentWithinput handleSearchToggle={handleSearchToggle} />
          </div>
        </div>
      </div>
    </CoveoEngineProvider>
  );
};

const SearchContentWithinput = ({
  handleSearchToggle,
}: {
  handleSearchToggle: (isOpen: boolean) => void;
}) => {
  const { getDictionaryValue } = useDictionary();
  const [commonSearches, setCommonSearches] = useState<string[] | null>(null);
  const hasSetCommonSearches = React.useRef(false);
  // const [isFirstSuggestion, setIsFirstSuggestion] = useState<boolean>(true);
  const engine = useCoveoEngine();
  const locationRef = React.useRef<LocationInputHandle>(null);
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;

  const handleCommonSearchResult = (result: string[]) => {
    if (!hasSetCommonSearches.current && result.length > 0) {
      setCommonSearches(result);
      hasSetCommonSearches.current = true;
    }
  };

  useEffect(() => {
    // On first load, fetch all data without a query
    if (engine) {
      engine.executeFirstSearch();
    }
  }, []);

  useEffect(() => {
    // @ts-expect-error: LocationInputHandle may not have focus, but SearchBar ref does
    locationRef.current?.focus?.();
  }, []);

  return (
    <>
      <SearchBar
        engine={engine}
        placeholder={{ value: getDictionaryValue('GlobalSearchPlaceholder') }}
        className="mb-12"
        designType="headerSearch"
        handleCommonSearchResult={handleCommonSearchResult}
        ref={locationRef}
        redirectUrl={contextWithSettings?.globalSearchPage?.value?.href || null}
        handleSearchToggle={handleSearchToggle}
      />
      <div className="mt-12">
        <h6 className="text-16 leading-22 font-centuryGothic font-bold text-gray-900 mb-4">
          <PlainTextWrapper field={{ value: getDictionaryValue('CommonSearches') }} />
        </h6>
        <ul className="list-none">
          {commonSearches &&
            commonSearches?.map((search) => (
              <li
                className="text-16 leading-22 font-normal font-centuryGothic text-gray-700 py-6"
                key={search}
              >
                <span
                  role="button"
                  tabIndex={0}
                  className="hover:underline underline-offset-[6px] cursor-pointer"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      locationRef.current?.setValue?.(search);
                      // @ts-expect-error: LocationInputHandle may not have focus, but SearchBar ref does
                      locationRef.current?.focus?.();
                      setTimeout(() => {
                        locationRef.current?.onSearchSubmit?.();
                      }, 0);
                    }
                  }}
                  onClick={() => {
                    locationRef.current?.setValue?.(search);
                    // @ts-expect-error: LocationInputHandle may not have focus, but SearchBar ref does
                    locationRef.current?.focus?.();
                    setTimeout(() => {
                      locationRef.current?.onSearchSubmit?.();
                    }, 0);
                  }}
                >
                  <PlainTextWrapper field={{ value: search }} />
                </span>
              </li>
            ))}
        </ul>
      </div>
    </>
  );
};
